'use client';

import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { motion } from 'framer-motion';
import { PortableText, type PortableTextComponents } from 'next-sanity';
import {
  richTextComponent,
  customRichTextComponent,
} from '@/sanity/sanityPortableText';
import { PortableTextBlock } from 'sanity';
import { cn } from '@/lib/utils';
import { usePathname } from 'next/navigation';
import { resolveI18nText, resolveI18nRichtext, NON_DEFAULT_LANGUAGES, DEFAULT_LANGUAGE } from '@/sanity/globals/I18n';
import { IFaqItem } from '@/sanity/lib/definitions';

const ease = [0.25, 0.1, 0.25, 1] as const;

const faqAnswerComponents = customRichTextComponent({
  block: {
    h3: ({ children }) => (
      <h3 className="mb-0 text-[2.5rem] font-semibold leading-[1.2] text-black">
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 className="mb-0 text-[2.25rem] font-semibold leading-[1.2] text-black">
        {children}
      </h4>
    ),
  },
});

function PlusIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      className="flex-shrink-0 w-[2rem] h-[2rem]"
    >
      <line
        x1="8"
        y1="1"
        x2="8"
        y2="15"
        stroke="black"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <line
        x1="1"
        y1="8"
        x2="15"
        y2="8"
        stroke="black"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MinusIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      className="flex-shrink-0 w-[2rem] h-[2rem]"
    >
      <line
        x1="1"
        y1="8"
        x2="15"
        y2="8"
        stroke="black"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

interface IFaqSectionData {
  faqChip?: string;
  faqTitle?: PortableTextBlock[];
  faqItems?: IFaqItem[];
}

export default function FaqSection({
  data,
  className,
  defaultOpenFirst,
  titleComponents = richTextComponent,
}: {
  data: IFaqSectionData;
  className?: string;
  defaultOpenFirst?: boolean;
  titleComponents?: PortableTextComponents;
}) {
  const pathname = usePathname();
  const language = NON_DEFAULT_LANGUAGES.find(
    (l) => pathname.startsWith(`/${l}/`) || pathname === `/${l}`,
  ) ?? DEFAULT_LANGUAGE;
  if (!data?.faqTitle && !data?.faqItems?.length) return null;

  return (
    <section
      className={cn(
        'relative w-full flex flex-col items-center bg-white pb-[8rem] lg:pb-[15rem] px-[2rem] lg:px-[6rem]',
        className,
      )}
    >
      <div className="w-[min(80rem,100%)] flex flex-col gap-[6rem] lg:gap-[8rem] items-center">
        {(data?.faqChip || data?.faqTitle) && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, ease }}
            className="flex flex-col items-center gap-[1.5rem]"
          >
            {data.faqChip && (
              <p className="mb-0 font-blacker italic text-[2rem] leading-[1.5] text-black">
                {data.faqChip}
              </p>
            )}
            {data.faqTitle && (
              <div className="richtext prose-h2:text-[4rem] lg:prose-h2:text-[6rem] prose-h2:font-medium prose-h2:leading-[1.15] prose-h2:tracking-[-0.3rem] text-center flex flex-col items-center">
                <PortableText
                  value={data.faqTitle}
                  components={titleComponents}
                />
              </div>
            )}
          </motion.div>
        )}

        {!!data?.faqItems?.length && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease, delay: 0.1 }}
            className="w-full"
          >
            <AccordionPrimitive.Root
              type="single"
              collapsible
              defaultValue={
                defaultOpenFirst && data.faqItems?.length
                  ? (data.faqItems[0]._key ?? '0')
                  : undefined
              }
              className="flex flex-col gap-[2rem]"
            >
              {data.faqItems.map((item, index) => {
                const key = item._key ?? String(index);
                const question = resolveI18nText(item.question, item.questionI18n, language);
                const answer = resolveI18nRichtext(item.answer, item.answerI18n, language);
                return (
                  <AccordionPrimitive.Item
                    key={key}
                    value={key}
                    className="group/item rounded-[0.25rem] border border-[rgba(0,0,0,0.1)] data-[state=open]:border-transparent data-[state=open]:bg-gradient-to-b data-[state=open]:from-[rgba(0,0,0,0.05)] data-[state=open]:to-[rgba(0,0,0,0.03)] transition-colors"
                  >
                    <AccordionPrimitive.Header>
                      <AccordionPrimitive.Trigger className="group/trigger flex w-full items-center gap-[3rem] p-[3rem] text-left outline-none">
                        <p className="mb-0 flex-1 text-[2.25rem] font-normal leading-[1.12] text-black">
                          {question}
                        </p>
                        <div className="flex-shrink-0">
                          <div className="group-data-[state=open]/trigger:hidden">
                            <PlusIcon />
                          </div>
                          <div className="hidden group-data-[state=open]/trigger:block">
                            <MinusIcon />
                          </div>
                        </div>
                      </AccordionPrimitive.Trigger>
                    </AccordionPrimitive.Header>
                    <AccordionPrimitive.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                      {answer && (
                        <div className="px-[3rem] pb-[3rem]">
                          <div className="richtext richtext-start prose-p:mb-0 prose-p:text-[2.25rem] prose-p:leading-[1.5] prose-p:text-black/50">
                            <PortableText
                              value={answer}
                              components={faqAnswerComponents}
                            />
                          </div>
                        </div>
                      )}
                    </AccordionPrimitive.Content>
                  </AccordionPrimitive.Item>
                );
              })}
            </AccordionPrimitive.Root>
          </motion.div>
        )}
      </div>
    </section>
  );
}
